import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginRequest, loginRequestSchema } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { FormError } from '../components/FormError';
import { PublicLayout } from '../components/PublicLayout';
import { Button } from '../components/ui/button';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { useAuthState } from '../lib/auth';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuthState();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const form = useForm<LoginRequest>({ resolver: zodResolver(loginRequestSchema) });
  const login = useMutation({
    mutationFn: (values: LoginRequest) => api.auth.login(values),
    onSuccess: () => navigate(from, { replace: true }),
  });

  if (auth === 'signedIn' && !login.isPending) return <Navigate to={from} replace />;

  return (
    <PublicLayout title={t('login.title')}>
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((v) => login.mutate(v))}
      >
        <FormError error={login.error} />
        <Field label={t('fields.email')} error={form.formState.errors.email} kind="email">
          <Input type="email" autoComplete="email" inputMode="email" {...form.register('email')} />
        </Field>
        <Field label={t('fields.password')} error={form.formState.errors.password}>
          <Input type="password" autoComplete="current-password" {...form.register('password')} />
        </Field>
        <Button type="submit" block disabled={login.isPending}>
          {login.isPending ? t('login.submitting') : t('login.submit')}
        </Button>
      </form>
      <p className="text-center text-sm text-muted">
        {t('login.noAccount')}{' '}
        <Link to="/register" className="font-medium text-accent underline">
          {t('login.register')}
        </Link>
      </p>
    </PublicLayout>
  );
}
